import { ChevronDown, ChevronUp, Search, X } from "lucide-react";
import { useMemo, useState, type JSX, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";

import { PageHeader } from "@/shared/components/PageHeader";
import { Pagination } from "@/shared/components/Pagination";
import { moneyFromString, parseMoneyAmount } from "@/shared/lib/money";

import { useRevenueReport } from "../api/use-revenue-report";
import { RevenueKpiStrip } from "../components/RevenueKpiStrip";
import {
  defaultRevenueFilter,
  toggleSort,
  type RevenueFilterState,
  type RevenueSortKey,
} from "../lib/revenue-filter";

function RevenueSkeleton(): JSX.Element {
  return (
    <div className="space-y-2 p-4" data-testid="revenue-loading">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="h-[var(--row-height-default)] animate-pulse rounded-[var(--r)] bg-[var(--surface-sunken)]"
        />
      ))}
    </div>
  );
}

const SORTABLE_COLUMNS: RevenueSortKey[] = ["settle_time", "order_no", "gross", "fees", "net"];

interface SortIconProps {
  colKey: RevenueSortKey;
  sortKey: RevenueSortKey;
  sortDir: "asc" | "desc";
}

function SortIcon({ colKey, sortKey, sortDir }: SortIconProps): JSX.Element | null {
  if (sortKey !== colKey) return null;
  return sortDir === "asc" ? (
    <ChevronUp className="ml-1 inline h-3.5 w-3.5" aria-hidden />
  ) : (
    <ChevronDown className="ml-1 inline h-3.5 w-3.5" aria-hidden />
  );
}

export function RevenueReportPage(): JSX.Element {
  const { t } = useTranslation(["revenue_report", "nav", "common"]);
  const [filter, setFilter] = useState<RevenueFilterState>(defaultRevenueFilter);
  const [searchInput, setSearchInput] = useState(filter.search);

  const { data, isLoading, isError } = useRevenueReport(filter);

  const total = data?.total ?? 0;
  const rows = useMemo(() => data?.rows ?? [], [data?.rows]);
  const summary = data?.summary ?? [];

  function handleSort(key: RevenueSortKey): void {
    setFilter((prev) => toggleSort(prev, key));
  }

  function submitSearch(): void {
    setFilter((prev) => ({ ...prev, search: searchInput.trim(), offset: 0 }));
  }

  function clearSearch(): void {
    setSearchInput("");
    setFilter((prev) => ({ ...prev, search: "", offset: 0 }));
  }

  function onSearchKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === "Enter") {
      e.preventDefault();
      submitSearch();
    }
  }

  function handleDateFrom(value: string): void {
    setFilter((prev): RevenueFilterState => {
      const next: RevenueFilterState = { ...prev, offset: 0 };
      if (value) {
        next.dateFrom = value;
      } else {
        delete next.dateFrom;
      }
      return next;
    });
  }

  function handleDateTo(value: string): void {
    setFilter((prev): RevenueFilterState => {
      const next: RevenueFilterState = { ...prev, offset: 0 };
      if (value) {
        next.dateTo = value;
      } else {
        delete next.dateTo;
      }
      return next;
    });
  }

  function handleMarketplace(value: string): void {
    setFilter((prev): RevenueFilterState => {
      const next: RevenueFilterState = { ...prev, offset: 0 };
      if (value) {
        next.marketplace = value;
      } else {
        delete next.marketplace;
      }
      return next;
    });
  }

  return (
    <div data-testid="revenue-report-page">
      <PageHeader
        breadcrumb={
          <>
            <span>{t("nav:group.reports")}</span>
            <span className="text-[var(--text-subtle)]">/</span>
            <span className="text-[var(--text-secondary)]">{t("nav:item.revenue_report")}</span>
          </>
        }
        title={t("revenue_report:page.title")}
        subtitle={t("revenue_report:page.subtitle", { count: total })}
      />

      {summary.length > 0 && <RevenueKpiStrip summary={summary} />}

      {/* Toolbar */}
      <div className="mb-[var(--sp-4)] flex flex-wrap items-center gap-[var(--sp-2)]">
        {/* Date range */}
        <label className="flex items-center gap-[var(--sp-2)] text-[13px] text-[var(--text-subtle)]">
          {t("revenue_report:toolbar.date_from")}
          <input
            type="date"
            value={filter.dateFrom ?? ""}
            onChange={(e) => handleDateFrom(e.target.value)}
            data-testid="revenue-date-from"
            className="h-9 rounded-[var(--r)] border border-[var(--border-default)] bg-[var(--surface-raised)] px-[var(--sp-2)] text-sm text-[var(--text-primary)] outline-none transition focus:border-[var(--brand-500)] focus-ring"
          />
        </label>
        <label className="flex items-center gap-[var(--sp-2)] text-[13px] text-[var(--text-subtle)]">
          {t("revenue_report:toolbar.date_to")}
          <input
            type="date"
            value={filter.dateTo ?? ""}
            onChange={(e) => handleDateTo(e.target.value)}
            data-testid="revenue-date-to"
            className="h-9 rounded-[var(--r)] border border-[var(--border-default)] bg-[var(--surface-raised)] px-[var(--sp-2)] text-sm text-[var(--text-primary)] outline-none transition focus:border-[var(--brand-500)] focus-ring"
          />
        </label>

        {/* Marketplace select */}
        <label className="flex items-center gap-[var(--sp-2)] text-[13px] text-[var(--text-subtle)]">
          {t("revenue_report:toolbar.marketplace")}
          <select
            value={filter.marketplace ?? ""}
            onChange={(e) => handleMarketplace(e.target.value)}
            data-testid="revenue-marketplace"
            className="h-9 rounded-[var(--r)] border border-[var(--border-default)] bg-[var(--surface-raised)] px-[var(--sp-2)] text-sm text-[var(--text-primary)] outline-none transition focus:border-[var(--brand-500)] focus-ring"
          >
            <option value="">{t("revenue_report:toolbar.all_marketplaces")}</option>
            <option value="shopee">Shopee</option>
            <option value="lazada">Lazada</option>
            <option value="tiktok">TikTok</option>
          </select>
        </label>

        {/* Search */}
        <div className="relative min-w-[240px] max-w-md flex-1">
          <Search
            className="pointer-events-none absolute left-[13px] top-1/2 h-[17px] w-[17px] -translate-y-1/2 text-[var(--text-subtle)]"
            aria-hidden
          />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={onSearchKeyDown}
            placeholder={t("revenue_report:toolbar.search_placeholder")}
            data-testid="revenue-search"
            className="h-9 w-full rounded-[var(--r)] border border-[var(--border-default)] bg-[var(--surface-raised)] pl-[38px] pr-9 text-sm text-[var(--text-primary)] outline-none transition focus:border-[var(--brand-500)] focus-ring"
          />
          {searchInput && (
            <button
              type="button"
              onClick={clearSearch}
              aria-label={t("common:action.clear")}
              className="absolute right-2.5 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-[var(--r-sm)] text-[var(--text-subtle)] transition hover:bg-[var(--surface-sunken)] focus-ring"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          )}
        </div>
      </div>

      {/* Content states */}
      {isError ? (
        <div
          className="rounded-[var(--r-md)] border border-[var(--border-subtle)] bg-[var(--surface-raised)] py-16 text-center text-sm text-[var(--text-secondary)] shadow-[var(--sh-1)]"
          data-testid="revenue-error"
        >
          {t("revenue_report:state.error")}
        </div>
      ) : isLoading ? (
        <div className="rounded-[var(--r-md)] border border-[var(--border-subtle)] bg-[var(--surface-raised)] shadow-[var(--sh-1)]">
          <RevenueSkeleton />
        </div>
      ) : rows.length === 0 ? (
        <div
          className="rounded-[var(--r-md)] border border-[var(--border-subtle)] bg-[var(--surface-raised)] py-16 text-center shadow-[var(--sh-1)]"
          data-testid="revenue-empty"
        >
          <p className="text-sm font-medium text-[var(--text-secondary)]">
            {t("revenue_report:state.empty")}
          </p>
        </div>
      ) : (
        <>
          {/* Table */}
          <div className="overflow-x-auto rounded-[14px] border border-[var(--border-subtle)] shadow-[var(--sh-1)]">
            <table className="min-w-full border-collapse bg-[var(--surface-raised)]">
              <thead className="border-b border-[var(--border-subtle)] bg-[var(--surface-raised)]">
                <tr>
                  {SORTABLE_COLUMNS.map((key) => (
                    <th
                      key={key}
                      data-testid={`revenue-th-${key}`}
                      className="h-11 cursor-pointer select-none whitespace-nowrap px-[var(--sp-4)] text-left text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] transition hover:text-[var(--text-primary)]"
                      onClick={() => handleSort(key)}
                    >
                      {t(`revenue_report:column.${key}`)}
                      <SortIcon colKey={key} sortKey={filter.sortKey} sortDir={filter.sortDir} />
                    </th>
                  ))}
                  {/* Non-sortable columns */}
                  {(["marketplace", "seller_name", "currency", "packages"] as const).map((key) => (
                    <th
                      key={key}
                      data-testid={`revenue-th-${key}`}
                      className="h-11 select-none whitespace-nowrap px-[var(--sp-4)] text-left text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)]"
                    >
                      {t(`revenue_report:column.${key}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-subtle)]">
                {rows.map((r, idx) => (
                  <tr
                    key={r.order_no}
                    data-testid={`revenue-row-${idx + 1}`}
                    data-order-no={r.order_no}
                    className="h-[var(--row-height-default)] transition hover:bg-[var(--surface-spotlight)]"
                  >
                    <td className="whitespace-nowrap px-[var(--sp-4)] text-sm text-[var(--text-secondary)]">
                      {new Date(r.settle_time).toLocaleDateString()}
                    </td>
                    <td className="whitespace-nowrap px-[var(--sp-4)] text-sm font-semibold text-[var(--text-primary)]">
                      {r.order_no}
                    </td>
                    <td
                      className="num whitespace-nowrap px-[var(--sp-4)] text-right text-sm font-semibold text-[var(--text-primary)]"
                      data-testid={`revenue-gross-${idx + 1}`}
                    >
                      {moneyFromString(r.gross, r.currency)}
                    </td>
                    <td
                      className="num whitespace-nowrap px-[var(--sp-4)] text-right text-sm"
                      data-testid={`revenue-fees-${idx + 1}`}
                      style={{
                        color:
                          parseMoneyAmount(r.fees) < 0
                            ? "var(--intent-danger-fg)"
                            : "var(--text-primary)",
                      }}
                    >
                      {moneyFromString(r.fees, r.currency)}
                    </td>
                    <td
                      className="num whitespace-nowrap px-[var(--sp-4)] text-right text-sm font-semibold text-[var(--text-primary)]"
                      data-testid={`revenue-net-${idx + 1}`}
                    >
                      {moneyFromString(r.net, r.currency)}
                    </td>
                    <td className="whitespace-nowrap px-[var(--sp-4)] text-sm text-[var(--text-secondary)]">
                      {r.marketplace}
                    </td>
                    <td className="whitespace-nowrap px-[var(--sp-4)] text-sm text-[var(--text-secondary)]">
                      {r.seller_name}
                    </td>
                    <td className="whitespace-nowrap px-[var(--sp-4)] text-sm text-[var(--text-secondary)]">
                      {r.currency}
                    </td>
                    <td className="num whitespace-nowrap px-[var(--sp-4)] text-right text-sm text-[var(--text-secondary)]">
                      {r.package_count}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            total={total}
            offset={filter.offset}
            limit={filter.limit}
            onChange={({ offset, limit }) => setFilter((prev) => ({ ...prev, offset, limit }))}
          />
        </>
      )}
    </div>
  );
}
